import { formatLongCalendarDate } from "../../../../../components/audit/auditLog/auditDates";
import {
  type DeletedWorkflowStepsCard,
  type WorkflowDeletion,
} from "../../lib/deletedWorkflowSteps";
import {
  type DeterministicBookend,
  type DeterministicOperationRow,
  type DeterministicStepList,
  type PopulationStatement,
} from "../../lib/deterministicSteps";
import { AnalyticsStepRow } from "../AnalyticsStepRow";

interface DeterministicStepsPanelProps {
  steps: DeterministicStepList;
  population: PopulationStatement;
  isLoading: boolean;
  /**
   * True where a read the list is assembled from failed — the run's operations,
   * its verdict, or the cards that say which of its steps hold evidence.
   *
   * A boolean rather than the failure: the panel says the same thing whichever
   * way the read failed, and the wording is this pane's rather than the
   * server's. What it buys is the refusal below — a count and an empty state are
   * both claims about the run, and neither can be made from a list this build
   * assembled out of a read that did not answer.
   *
   * It is the condition on the notice alone. The count is refused on a second
   * condition too, which rides the list as `stepCountUnestablished`: the two
   * part company on a verdict naming no run, where nothing failed and there is
   * still no figure to state.
   */
  stepsUnreadable: boolean;
  /**
   * Whether the column's workflow still exists, and the copy for the state where
   * it does not.
   *
   * The deleted arm outranks every other state this panel can draw, including
   * `stepsUnreadable`, because it is the only one of them that is a fact about
   * the evidence rather than a report on a read. The workflow's runs are deleted
   * rather than unreachable, and a notice saying the read failed would invite a
   * retry for something that is gone.
   */
  workflowDeletion: WorkflowDeletion;
  /** The step whose card the viewer is showing. */
  selectedKey: string | null;
  onSelectStep: (row: DeterministicOperationRow) => void;
  /**
   * Absent where the host cannot write the flag — a session whose sign-off is
   * complete is the case that reaches it. The star is then drawn read-only
   * rather than dropped: which steps reached the workpaper is part of the
   * account of the run, and a closed session is exactly when that account is
   * read.
   */
  onToggleStar?: (row: DeterministicOperationRow) => void;
}

const STEPS_UNREADABLE = "The run's steps could not be read.";

/** The treatment of the tab's headings: the steps list's and each bookend's. */
const SMALL_HEADING =
  "text-[11px] font-semibold uppercase tracking-wide text-stone-500";

/**
 * What an empty list says, which turns on why it is empty.
 *
 * "No steps were recorded" is a claim about the run, and this pane can reach an
 * empty list without the run having recorded nothing: a run whose every
 * operation was a publish step with no evidence to show, and which composed no
 * bookends either, has every row withheld here. Saying the run recorded nothing
 * would then be this build's own filter reported as the run's account of itself.
 *
 * The second reading is ordinary and is worded as one. Nothing failed, nothing
 * is missing — the run's steps simply have no evidence behind them to open.
 */
function emptyListCopy(steps: DeterministicStepList): string {
  return steps.loggedOperationCount === 0
    ? "No steps were recorded for this run."
    : "This run's steps have no evidence to show.";
}

/**
 * The run's steps, as the middle pane's second view for an analytics column.
 *
 * **One list of the workflow's steps, imports included, in run order** — less
 * the publish steps the run gave no card, which have nothing to open — with
 * Midship's two composed rows below it, what the run was handed and then what
 * it returned, drawn as plain text because they open nothing.
 *
 * **A row is a glyph, a name, a timestamp and a star.** The rule the step
 * applied, the counts it moved and the sentence the engine wrote about it are all
 * on the card the server rendered for that step, and selecting the row opens it.
 * Drawing any of that here as well would be two renderings of one step's facts,
 * which is the failure the card exists to prevent — so this pane draws no rule,
 * no table and no preview of its own.
 *
 * **This is transparency, not a correction path.** Nothing here edits the
 * population figure, reassigns an output's role or pins a step. The star is the
 * single write, and it is not a correction: it says which steps reach the
 * workpaper, not that a step was wrong. An auditor who disagrees with the run's
 * account of itself overrides the verdict on the review pane, where the
 * disagreement is recorded as theirs — a control here would let the run's own
 * account be rewritten in place, leaving nothing to show it had been.
 *
 * **A failed read is reported as one and never counted.** The heading's "(3)"
 * and "no steps were recorded" are both claims about what the run did, and a
 * read that did not answer supports neither.
 *
 * **A deleted workflow is read off the column's status and never off the list.**
 * Its runs are cascade-deleted upstream, and a read of them would fail as an
 * engine outage does, so only the status reports the deletion. What list
 * remains is the state the deleted card replaces, which is why the card is
 * chosen before the list is examined at all.
 */
export function DeterministicStepsPanel({
  steps,
  population,
  isLoading,
  stepsUnreadable,
  workflowDeletion,
  selectedKey,
  onSelectStep,
  onToggleStar,
}: DeterministicStepsPanelProps) {
  // The status joins the reads the list is composed from, because the list
  // reads the same whether or not the workflow behind it still exists. Drawing
  // it while that question is still out is the read-driven failure, one frame
  // at a time. A deleted card still waiting on its manifest waits here too.
  if (
    isLoading ||
    workflowDeletion.kind === "unsettled" ||
    (workflowDeletion.kind === "deleted" &&
      workflowDeletion.copy.card.kind === "pending")
  ) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-sm text-stone-400">
        Loading the run's steps…
      </div>
    );
  }

  // Ahead of the empty state as well as the list, because a deleted workflow
  // reaches both: the verdict's bookends compose a short list with the run's
  // operations withheld, and a verdict whose own reads came back short composes
  // none. Neither is what the auditor needs to be told.
  if (
    workflowDeletion.kind === "deleted" &&
    workflowDeletion.copy.card.kind === "ready"
  ) {
    // The population stays. It is the verdict's own figure, counted before the
    // deletion and carried on the record Midship holds, so it survives exactly
    // as the conclusion it supports does.
    const deletedPopulationLine = populationSentence(population);
    return (
      <div className="h-full overflow-y-auto px-4 py-3">
        {deletedPopulationLine !== null && (
          <div className="mb-2 text-xs text-stone-400">
            {deletedPopulationLine}
          </div>
        )}

        <DeletedWorkflowCard copy={workflowDeletion.copy.card} />
      </div>
    );
  }

  if (steps.stepCount === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-center text-sm text-stone-400">
        {stepsUnreadable ? STEPS_UNREADABLE : emptyListCopy(steps)}
      </div>
    );
  }

  // A figure over rows this build established nothing about is a claim about
  // the run it is in no position to make, so the heading states its count only
  // where the reads behind the list answered. The rows themselves are drawn
  // either way — a read that did not answer is reported, never acted on, so no
  // row is filtered out on the strength of it, including publish rows the
  // backend may genuinely have given no card.
  const countEstablished = !stepsUnreadable && !steps.stepCountUnestablished;

  // Said here only where the inputs bookend does not say it, because stating
  // it twice invites a reader to look for the difference between them. The
  // Inputs row carries the figure only where the verdict does: the backend
  // composes it from the verdict's own total, and an inconclusive verdict
  // carries none even over a run that resolved one, so a resolved figure alone
  // would hide the population on exactly the cells where it matters most. The
  // row must also be on screen: a verdict carrying no trail — an auditor's
  // override supersedes the judged one and composes none — draws no bookend to
  // carry the figure. The override clause is a guard: a figure the auditor
  // typed is never credited to the run's row, even if an override comes to
  // carry a trail.
  const bookendStatesPopulation =
    steps.inputs.length > 0 &&
    population.statedOnVerdict &&
    population.provenance.kind !== "auditorOverride";
  const populationLine = bookendStatesPopulation
    ? null
    : populationSentence(population);

  return (
    <div className="h-full space-y-4 overflow-y-auto px-4 py-3">
      {(stepsUnreadable || populationLine !== null) && (
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-stone-400">
          {stepsUnreadable && <span>{STEPS_UNREADABLE}</span>}
          {populationLine !== null && <span>{populationLine}</span>}
        </div>
      )}

      {steps.rows.length > 0 && (
        <section>
          <h4 className={`mb-2 px-0.5 ${SMALL_HEADING}`}>
            Steps{countEstablished && ` (${steps.rows.length})`}
            {steps.runDate !== null && (
              <span className="font-normal normal-case tracking-normal text-stone-400">
                {` · Ran ${formatLongCalendarDate(steps.runDate)}`}
              </span>
            )}
          </h4>
          <ol className="space-y-1.5">
            {steps.rows.map((row) => (
              <AnalyticsStepRow
                key={row.key}
                stepName={row.stepName}
                operation={row.operation}
                completion={row.completion}
                runDate={steps.runDate}
                starred={row.citation ? row.citation.isKey === true : null}
                active={row.key === selectedKey}
                onSelect={() => onSelectStep(row)}
                onToggleStar={
                  onToggleStar ? () => onToggleStar(row) : undefined
                }
              />
            ))}
          </ol>
        </section>
      )}

      {[...steps.inputs, ...steps.outputs].map((bookend) => (
        <BookendLine key={bookend.key} bookend={bookend} />
      ))}
    </div>
  );
}

/**
 * The one card that stands in for the list once the workflow is gone.
 *
 * Prose in a card rather than a row, and deliberately not a step: a row would
 * take its place in an order and invite a selection, and there is no step record
 * behind it to open.
 *
 * Every sentence is rendered as it was handed over, and none is composed here:
 * the work-paper export states the same facts from the same source, and a
 * component that assembled its own wording would be the second one.
 */
function DeletedWorkflowCard({
  copy,
}: {
  copy: Extract<DeletedWorkflowStepsCard, { kind: "ready" }>;
}) {
  return (
    <div className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-3">
      <div className="text-sm font-medium text-stone-800">{copy.title}</div>
      <p className="mt-1.5 text-xs text-stone-500">{copy.body}</p>
      <p className="mt-1.5 text-xs text-stone-500">{copy.reassurance}</p>
    </div>
  );
}

/**
 * The population and where it came from, stated once for the run, or null
 * where there is no figure to state.
 *
 * Beside its source rather than on its own, because the figure is only as good
 * as its provenance: "42,318 rows" asserts a population, "42,318 rows, counted
 * at the Import step" shows one. A run that resolved no figure shows nothing —
 * a tab that fell back to counting rows on screen would report a findings-only
 * export of a dozen exceptions as a population of twelve.
 *
 * A run whose manifest could not be read says so, because that is a failed
 * read rather than a missing figure: only the run can settle whether it
 * resolved one, and its account of itself is the thing that did not arrive. A
 * cell no run reached says so too — there was no run to have resolved one.
 */
function populationSentence(population: PopulationStatement): string | null {
  const { provenance, total } = population;

  if (provenance.kind === "unreadable") {
    return "This run's population could not be read";
  }

  if (provenance.kind === "noRun") {
    return "No run has produced a population for this cell";
  }

  if (total === null) {
    return null;
  }

  const figure = total.toLocaleString();
  switch (provenance.kind) {
    case "step":
      return `Population ${figure} rows, counted at the ${provenance.stepName} step`;
    case "unnamedStep":
      return `Population ${figure} rows, counted at a step this run's log does not name`;
    case "inputParameter":
      return `Population ${figure} rows, from the ${provenance.paramKey} input`;
    case "populationFile":
      return `Population ${figure} rows, from the population file`;
    case "auditorOverride":
      return `Population ${figure} rows, from the auditor's override`;
    case "unstated":
      return `Population ${figure} rows`;
    // A provenance added to the union has to say here how it reads. Falling
    // through would draw a figure with no source beside it, which is the one
    // thing this line exists to prevent, and nothing on screen would show a
    // reading had gone missing.
    default: {
      const unhandled: never = provenance;
      return unhandled;
    }
  }
}

/**
 * One of the rows Midship composed about the run: a heading and a line of
 * prose, below the steps.
 *
 * Drawn as text and not as a step: it opens nothing, and a bordered row with a
 * tile would offer a selection with nothing behind it. It carries no star for
 * the same reason — a composed row cites nothing, so there is nothing for the
 * include flag to hang on. The heading is the server's, as is the line.
 */
function BookendLine({ bookend }: { bookend: DeterministicBookend }) {
  return (
    <div className="px-0.5">
      <h4 className={SMALL_HEADING}>{bookend.title}</h4>
      {bookend.detail && (
        <p className="mt-1 text-xs text-stone-500">{bookend.detail}</p>
      )}
    </div>
  );
}
